import { useState, useContext, useEffect, createContext } from "react";
import { client } from "../apollo";
import { ExpectedError } from "../errors/ExpectedError";
import {
  AddItemToCheckoutDocument,
  AddItemToCheckoutMutation,
  AddItemToCheckoutMutationVariables,
  CheckoutFragment,
  CheckoutLineItemInput,
  CreateCheckoutDocument,
  CreateCheckoutMutation,
  CreateCheckoutMutationVariables,
  GetCheckoutByIdDocument,
  GetCheckoutByIdQuery,
  GetCheckoutByIdQueryVariables,
  LineItemFragment,
  RemoveItemFromCheckoutDocument,
  RemoveItemFromCheckoutMutation,
  RemoveItemFromCheckoutMutationVariables,
  UpdateItemsInCheckoutDocument,
  UpdateItemsInCheckoutMutation,
  UpdateItemsInCheckoutMutationVariables,
} from "../generated/shopify";

const LOCAL_STORAGE_KEY = "faceguy-shopify-checkout-id-13781368131";

interface ContextState {
  /** lineItems in this object are the lineItems fetched from the checkout that was catched in local storage */
  checkoutInfo: CheckoutFragment | null;
  lineItems: LineItemFragment[];
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  createCheckout: () => Promise<CheckoutFragment>;
  addItemsToCheckout: (lineItems: CheckoutLineItemInput[]) => Promise<void>;
  removeItemFromCheckout: (lineItemId: string) => Promise<void>;
  setQuantityOfItemInCheckout: (
    lineItem: LineItemFragment,
    quantity: number
  ) => Promise<void>;
  getTotalPrice: () => number;
}

const ShopContext = createContext<ContextState | null>(null);

export const ShopProvider: React.FC = ({ children }) => {
  const [checkoutInfo, setCheckoutInfo] = useState<CheckoutFragment | null>(
    null
  );
  const [lineItems, setLineItems] = useState<LineItemFragment[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const fetchCheckoutInfo = async (
    id: string
  ): Promise<CheckoutFragment | undefined> => {
    try {
      const checkout = await client.query<
        GetCheckoutByIdQuery,
        GetCheckoutByIdQueryVariables
      >({
        query: GetCheckoutByIdDocument,
        variables: {
          id,
        },
      });

      if (
        checkout.error ||
        checkout.errors ||
        checkout.data.node?.__typename !== "Checkout"
      )
        throw new Error("Errors or incorrect data returned from request");

      return checkout.data.node;
    } catch (err) {
      return undefined;
    }
  };

  const createCheckout = async (): Promise<CheckoutFragment> => {
    const checkout = await client.mutate<
      CreateCheckoutMutation,
      CreateCheckoutMutationVariables
    >({
      mutation: CreateCheckoutDocument,
      variables: {
        input: {},
      },
    });
    if (checkout.errors || !checkout.data?.checkoutCreate?.checkout?.id)
      throw new Error("Something went wrong while creating checkout");

    const { checkout: checkoutResponse } = checkout.data.checkoutCreate;
    localStorage.setItem(LOCAL_STORAGE_KEY, checkoutResponse.id);
    setCheckoutInfo(checkoutResponse);
    return checkoutResponse;
  };

  useEffect(() => {
    const fetchCheckout = async (): Promise<void> => {
      try {
        const id = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!id) throw new ExpectedError();

        const checkout = await fetchCheckoutInfo(id);
        if (!checkout) throw new ExpectedError();
        setCheckoutInfo(checkout);
        setLineItems(checkout.lineItems.edges.map((x) => x.node));
      } catch (err) {
        if (!(err instanceof ExpectedError)) console.error(err);
        await createCheckout();
      }
    };

    fetchCheckout();
  }, []);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const addItemsToCheckout = async (
    lineItems: CheckoutLineItemInput[]
  ): Promise<void> => {
    const response = await client.mutate<
      AddItemToCheckoutMutation,
      AddItemToCheckoutMutationVariables
    >({
      mutation: AddItemToCheckoutDocument,
      variables: {
        checkoutId: checkoutInfo?.id ?? (await createCheckout()).id,
        lineItems,
      },
    });
    if (
      response.errors ||
      !response.data?.checkoutLineItemsAdd?.checkout?.lineItems
    )
      throw new Error("Something went wrong while adding an item");

    setLineItems(
      response.data?.checkoutLineItemsAdd.checkout.lineItems.edges.map(
        (x) => x.node
      )
    );
    setIsCartOpen(true);
  };

  const removeItemFromCheckout = async (lineItemId: string): Promise<void> => {
    const response = await client.mutate<
      RemoveItemFromCheckoutMutation,
      RemoveItemFromCheckoutMutationVariables
    >({
      mutation: RemoveItemFromCheckoutDocument,
      variables: {
        checkoutId: checkoutInfo?.id ?? (await createCheckout()).id,
        lineItems: [lineItemId],
      },
    });

    if (response.errors || !response.data?.checkoutLineItemsRemove?.checkout)
      throw new Error("Something went wrong while removing item from cart");

    setLineItems(
      response.data.checkoutLineItemsRemove.checkout.lineItems.edges.map(
        (x) => x.node
      )
    );
  };

  const setQuantityOfItemInCheckout = async (
    lineItem: LineItemFragment,
    quantity: number
  ): Promise<void> => {
    try {
      const response = await client.mutate<
        UpdateItemsInCheckoutMutation,
        UpdateItemsInCheckoutMutationVariables
      >({
        mutation: UpdateItemsInCheckoutDocument,
        variables: {
          checkoutId: checkoutInfo?.id ?? (await createCheckout()).id,
          lineItems: [
            {
              id: lineItem.id,
              quantity,
              variantId: lineItem.variant?.id ?? undefined,
            },
          ],
        },
      });

      if (response.errors || !response.data?.checkoutLineItemsUpdate?.checkout)
        throw new Error("Something went wrong updating quantity of item.");

      setLineItems(
        response.data.checkoutLineItemsUpdate.checkout.lineItems.edges.map(
          (x) => x.node
        )
      );
    } catch (err) {
      console.log(err);
    }
  };

  const getTotalPrice = () =>
    lineItems.reduce((value, newItem) => {
      if (!newItem.variant) throw new Error("No variant found");
      return value + newItem.variant.priceV2.amount * newItem.quantity;
    }, 0);

  return (
    <ShopContext.Provider
      value={{
        checkoutInfo,
        lineItems,
        addItemsToCheckout,
        removeItemFromCheckout,
        setQuantityOfItemInCheckout,
        createCheckout,
        isCartOpen,
        openCart,
        closeCart,
        getTotalPrice,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export const useShop = () => {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("No provider was provided for useShop");
  return ctx;
};
